/**
 * Game options for creating a new game
 */
export interface GameOptions {
  /** Name of the game host/creator */
  playerName: string;
  /** Game mode (standard, sprint, etc.) */
  gameMode: "standard" | "survivor" | "sprint" | "hot_potato" | "team";
  /** Maximum number of players allowed */
  maxPlayers: number;
  /** Starting level (1-10) */
  startingLevel?: number;
  /** Game end condition */
  gameEndType?: "none" | "frag" | "lines" | "time" | "score";
  /** Value for game end condition */
  gameEndValue?: number;
  /** Whether this is a dedicated server (host doesn't play) */
  isDedicatedServer?: boolean;
  /** Host's preferred frag announcer pack */
  fragAnnouncerPack?: string;
  /** Host's preferred block pack */
  blockPack?: string;
  /** Host's preferred canvas pack */
  canvasPack?: string;
}

/**
 * Represents the state of a player's board
 */
export interface BoardState {
  /** The grid cells (0 = empty, 1-7 = block colors) */
  grid: number[][];
  /** Width of the board */
  width: number;
  /** Height of the board */
  height: number;
  /** Current active piece */
  currentPiece?: {
    type: number; // quel
    rotation: number;
    x: number; // canvas x position (4-14 range)
    y: number; // canvas y position (12-32 range)
    color: number;
    posX?: number; // smooth position X
    posY?: number; // smooth position Y
  };
  /** Next piece to appear */
  nextPiece?: number;
  /** Lines that are currently being cleared (for animation) */
  clearingLines: number[];
  /** Pending garbage lines to be added */
  pendingGarbage: {
    lines: number;
    holes: number[];
  }[];
}

/**
 * Represents a player in the game
 */
export interface PlayerState {
  /** Unique connection identifier (PeerJS connection ID, matches Net_connection->id()) */
  connectionId: string;
  /** Server-assigned player ID for rejoin detection (matches original player_id, uint32_t) */
  playerId?: number;
  /** Player's display name */
  name: string;
  /** Player number/slot (0-5) - matches original canvas->num_player */
  numPlayer: number;
  /** Team number (0-8) */
  team: number;
  /** Persistent player hash for rejoin detection (matches original player_hash) */
  playerHash?: string;
  /** Whether player is gone/disconnected (matches original idle=3) */
  isGone?: boolean;
  /** Player's current board state */
  board: BoardState;
  /** Current score */
  score: number;
  /** Number of frags (kills) */
  frags?: number;
  /** Number of deaths */
  deaths?: number;
  /** Number of round wins (survivor mode) */
  roundWins?: number;
  /** Player number (0-5) of the last player who attacked this player (for frag tracking, matches original canvas->last_attacker) */
  lastAttackerNumPlayer?: number;
  /** Attack counts from each player (Record<numPlayer, count>) - for last attacker determination (matches original attacks[MAXPLAYERS]) */
  attackCounts?: Record<number, number>;
  /** Whether the player is active or eliminated */
  isActive: boolean;
  /** Player's current level */
  level: number;
  /** Number of lines cleared */
  lines: number;
  /** Current handicap level (0 = none) */
  handicap: number;
  /** Whether the player is currently attacking */
  isAttacking: boolean;
  /** Time of last activity (for AFK detection) */
  lastActivity?: number;
  /** Special player states */
  state?: "normal" | "ghost" | "dead" | "winner";
  /** Client-reported canvas state (WAITFORWINNER, WAITFORRESTART, etc.) */
  canvasState?: "playing" | "waitForWinner" | "waitForRestart" | "last";
  /** Whether this player is a dedicated server (not playing) */
  isDedicatedServer?: boolean;
  /** Player's preferred frag announcer pack ("default", "fireworks", etc.) */
  fragAnnouncerPack?: string;
  /** Player's preferred block pack ("default", "fancy", "cement", etc.) */
  blockPack?: string;
  /** Player's preferred canvas pack ("default", "icy", "levelBased", etc.) */
  canvasPack?: string;
  /** Pieces Per Minute (calculated every 128 frames) */
  ppm: number;
  /** Blocks Per Minute (calculated every 128 frames) */
  bpm: number;
}

/**
 * Attack information for sending lines to opponents
 */
export interface Attack {
  /** Attack type (matches original Attack_type enum: ATTACK_LINES, ATTACK_NONE, ATTACK_BLIND, ATTACK_FULLBLIND) */
  type: "normal" | "clean" | "potato" | "none" | "blind" | "fullblind";
  /** Attack parameter (matches original param int, used for blind time in ATTACK_BLIND) */
  param?: number;
  /** Number of lines to send (derived from nb, not in original Attack struct) */
  lines: number;
  /** Complexity of the attack (derived from nc, not in original Attack struct) */
  complexity: number;
  /** Source connection ID (not in original, but useful for routing) */
  sourceConnectionId: string;
  /** Target connection ID (or undefined for auto-targeting, not in original) */
  targetConnectionId?: string;
  /** Hole positions for each line (derived from hole_pos[], not in original Attack struct) */
  holePositions?: number[];
}

/**
 * Game message event types for displaying animated notifications
 * Uses discriminated union for type safety
 */
export type GameMessageEvent = 
  | {
      type: "frag";
      id: string;
      fraggerNumPlayer: number; // Player number (0-5) of the fragger (matches original design)
      fraggedNumPlayer: number; // Player number (0-5) of the victim (matches original design)
      timestamp: number;
    }
  | {
      type: "roundWin";
      id: string;
      winningTeam: number;
      winningTeamName: string;
      timestamp: number;
    }
  | {
      type: "roundLose";
      id: string;
      winningTeam: number;
      winningTeamName: string;
      timestamp: number;
    }
  | {
      type: "firstBlood";
      id: string;
      fraggerNumPlayer: number;
      timestamp: number;
    }
  | {
      type: "multiKill";
      id: string;
      fraggerNumPlayer: number;
      killCount: number;
      timestamp: number;
    }
  | {
      type: "killStreak";
      id: string;
      fraggerNumPlayer: number;
      streak: number;
      timestamp: number;
    };

/**
 * Game state for the entire game
 */
export interface GameState {
  /** Players indexed by numPlayer (0-5) - primary structure (matches original Canvas *list[MAXPLAYERS]) */
  players: Map<number, PlayerState>;
  /** Connections indexed by connectionId - for networking lookups only */
  connections: Map<string, PlayerState>;
  /** Random seed for game events */
  gameSeed: number;
  /** Whether the game is paused */
  isPaused: boolean;
  /** Current game mode */
  gameMode: string;
  /** Whether this client is the server/host */
  isServer: boolean;
  /** Connection ID of the local player */
  localConnectionId?: string;
  /** Connection ID of the room host/owner */
  hostConnectionId?: string;
  /** Server-assigned player ID for the local player (from P_PLAYERACCEPTED, matches original player_id) */
  localPlayerId?: number;
  /** Server-assigned position/slot for the local player (from P_PLAYERACCEPTED.pos) */
  localPlayerPosition?: number;
  /** Current game status */
  status: "waiting" | "starting" | "active" | "finished";
  /** Round sync state for transitions (matches original Canvas::State) */
  roundSyncState?: "playing" | "waitForWinner" | "waitForRestart" | "last";
  /** Whether this round has become competitive (survivor mode, matches original game->valid_frag) */
  validFrag?: boolean;
  /** Round number (matches original game->stats[GS::ROUND_NUMBER]) */
  roundNumber?: number;
  /** Time when the game started */
  startTime?: number;
  /** Countdown value (0 = no countdown, 500 = waiting to start, 499-1 = countdown in progress) */
  countdown?: number;
  /** Timestamp when countdown started (for synchronization) */
  countdownStartTime?: number;
  /** Game parameters */
  params?: {
    /** Whether level-ups are enabled */
    levelUp: boolean;
    /** Starting level */
    startLevel: number;
    /** Minimum combo size for attacks */
    comboMin: number;
    /** Whether handicaps are allowed */
    allowHandicap: boolean;
    /** Game end condition */
    gameEndType: "none" | "frag" | "lines" | "time" | "score";
    /** Value for game end condition */
    gameEndValue: number;
  };
  /** Chat messages */
  chatMessages?: {
    connectionId: string;
    text: string;
    timestamp: number;
    toTeam?: number;
  }[];
  /** Game messages for displaying animated notifications */
  gameMessages?: GameMessageEvent[];
  /** Maximum number of players allowed in the game */
  maxPlayers?: number;
  /** Watchers for each player (Map<numPlayer, Set<connectionId>>) - matches original Canvas::watchers */
  watchers?: Map<number, Set<string>>;
}

/**
 * Types of player moves
 */
export enum MoveType {
  MOVE_LEFT = 1,
  MOVE_RIGHT = 2,
  MOVE_DOWN = 3,
  DROP = 4,
  ROTATE_CW = 5,
  ROTATE_CCW = 6,
  HOLD = 7,
}

/**
 * Player movement data
 */
export interface PlayerMove {
  /** Type of move */
  type: MoveType;
  /** Timestamp when the move occurred */
  timestamp: number;
  /** Raw byte value from original packet (for exact reconstruction) */
  rawByte?: number;
}

/**
 * Different packet types for network communication
 */
export enum PacketTypes {
  WANT_JOIN = 41, // Client→Server (initial join request, matches original P_WANTJOIN)
  JOIN_REQUEST = 42, // Client→Server (player join request, matches original P_PLAYERWANTJOIN)
  JOIN_RESPONSE = 2, // Server→Client (join response, matches original P_PLAYERACCEPTED)
  GAMESERVER = 3, // Server→Client (sent on join, matches original P_GAMESERVER)
  PLAYER = 45, // Server→Client (notifies existing clients about new player, matches original P_PLAYER)
  PLAYER_MOVE = 4, // Server→Client (dispatched by server, matches original P_MOVES)
  CLIENT_MOVE = 30, // Client→Server (sent by clients, matches original P_CLIENTMOVES)
  PLAYER_LINES = 5, // Server→Client (dispatched by server, matches original P_LINES)
  CLIENT_LINES = 29, // Client→Server (sent by clients, matches original P_CLIENTLINES)
  CHAT_MESSAGE = 6,
  GAME_START = 7,
  GAME_PAUSE = 8, // Server→Client (dispatched by server, matches original P_PAUSE)
  CLIENT_PAUSE = 37, // Client→Server (sent by clients, matches original P_CLIENTPAUSE)
  PLAYER_STATE = 9,
  PLAYER_LEAVE = 10,
  GAME_OVER = 11,
  PING = 12,
  PONG = 13,
  GAME_SETTINGS = 14,
  DEAD = 15,
  CLIENT_DEAD = 16,
  CLIENT_STATE = 17,
  SERVER_STATE = 18,
  CLIENT_RESPAWN = 19,
  RESPAWN = 20,
  CHANGE_TEAM = 21,
  STAMP_BLOCK = 22, // Server→Client (dispatched by server, matches original P_STAMPBLOCK)
  CLIENT_STAMP_BLOCK = 31, // Client→Server (sent by clients, matches original P_CLIENTSTAMPBLOCK)
  GONE = 23, // Server→Client (dispatched by server, matches original P_GONE)
  CLIENT_GONE = 32, // Client→Server (sent by clients, matches original P_CLIENTGONE)
  REMOVE_BONUS = 24, // Server→Client (dispatched by server, matches original P_REMOVEBONUS)
  CLIENT_REMOVE_BONUS = 33, // Client→Server (sent by clients, matches original P_CLIENTREMOVEBONUS)
  FIRST_FRAG = 25, // Server→Client (dispatched by server, matches original P_FIRST_FRAG)
  CLIENT_FIRST_FRAG = 34, // Client→Server (sent by clients, matches original P_CLIENTFIRST_FRAG)
  DOWNLOAD_STATE = 26,
  PLAYER_STATS = 27,
  REJOIN = 28,
  START_WATCH = 35, // Server→Client (dispatched by server, matches original P_STARTWATCH)
  CLIENT_START_WATCH = 36, // Client→Server (sent by clients, matches original P_CLIENTSTARTWATCH)
  LOBBY_UPDATE = 46, // Server→Client (lobby updates: team changes, game end settings, matches original P_GAMESERVER but only for lobby)
  SERVER_RANDOM = 47, // Server→Client (new seed for round restart, matches original P_SERVERRANDOM)
  ENDGAME = 48, // Server→Client (end game signal, matches original P_ENDGAME)
  PLAYER_CANVAS_STATE = 49, // Server→Client (per-player canvas state, matches original P_STATE)
  GAME_MESSAGE = 50, // Server→Client (game message events: frag, roundWin, etc.)
}

/**
 * Game statistics for a player
 */
export interface PlayerStats {
  /** Total score */
  score: number;
  /** Number of lines cleared */
  lines: number;
  /** Number of tetrises (4 lines at once) */
  tetrises: number;
  /** Number of combos performed */
  combos: number;
  /** Longest combo achieved */
  maxCombo: number;
  /** Number of blocks placed */
  blocksPlaced: number;
  /** Number of pieces played */
  piecesPlayed: number;
  /** Game time in seconds */
  playTime: number;
  /** Lines sent to opponents */
  linesSent: number;
  /** Lines received from opponents */
  linesReceived: number;
  /** Number of attacks performed */
  attacks: number;
}

/**
 * Record of a game for history/replays
 */
export interface GameRecord {
  /** Unique ID for the game */
  id: string;
  /** Game mode that was played */
  gameMode: string;
  /** When the game was played */
  timestamp: number;
  /** Duration of the game in seconds */
  duration: number;
  /** Players who participated */
  players: {
    connectionId: string;
    name: string;
    team: number;
    stats: PlayerStats;
    ranking: number;
  }[];
  /** Winner connection ID or team */
  winner?: string;
  /** Whether the game was completed normally */
  completed: boolean;
}

/**
 * Configuration for the game client
 */
export interface ClientConfig {
  /** Visual settings */
  visual: {
    /** Whether to show shadows */
    shadows: boolean;
    /** Whether to use smooth animations */
    smooth: boolean;
    /** Whether to use horizontal piece repeating */
    horizontalRepeat: boolean;
    /** Whether to use vertical piece repeating */
    verticalRepeat: boolean;
  };
  /** Control mappings */
  controls: {
    /** Key bindings for different actions */
    keyBindings: Record<MoveType, number>;
  };
  /** Player name */
  playerName: string;
  /** Preferred team */
  preferredTeam: number;
  /** Whether to show chat messages */
  showChat: boolean;
  /** Whether to show player panes */
  showPlayers: boolean;
}

/**
 * Connection status for network peers
 */
export interface PeerConnectionStatus {
  /** Peer ID */
  peerId: string;
  /** Connection state */
  state: "connecting" | "connected" | "disconnected" | "failed";
  /** Ping time in milliseconds */
  ping?: number;
  /** Timestamp of last activity */
  lastActivity: number;
  /** Number of packets sent */
  packetsSent: number;
  /** Number of packets received */
  packetsReceived: number;
}
